import AccessTime from "@mui/icons-material/AccessTime";
import type { Theme } from "@mui/material";
import { Box, CircularProgress, useTheme } from "@mui/material";

import { dayjs } from "@thalamos/common";
import type {
  IncidentWorkItemWithContext,
  TranslationKeyAndOptions,
} from "@thalamos/vision-common";
import {
  getWorkflowTranslationContext,
  INCIDENT_DURATION_THRESHOLD_HOURS,
  tk,
} from "@thalamos/vision-common";

import { TypographyI18N } from "../../TypographyI18N/TypographyI18N.js";
import { FormLabel } from "../FormLabel/FormLabel.js";

export type IncidentDurationProps = {
  label?: TranslationKeyAndOptions;
  dateTimeLeft?: string;
  arrivalAtFirstPlaceOfSafetyDateTime?: string;
  incidentWorkItem?: IncidentWorkItemWithContext;
};

function getDurationParts(dateTimeLeft: string, dateTimeStart: string) {
  const incidentDateTimeLeft = dayjs(dateTimeLeft);
  const incidentDateTimeStart = dayjs(dateTimeStart);

  const diff = incidentDateTimeLeft.diff(incidentDateTimeStart);
  const dur = dayjs.duration(diff);
  const hours = Math.floor(dur.asHours());
  const minutes = dur.minutes();

  return { hours, minutes };
}

function getDurationBgColor(hours: number, minutes: number, theme: Theme) {
  if (hours >= INCIDENT_DURATION_THRESHOLD_HOURS || hours < 0) {
    return theme.palette.custom.error.light;
  }
  if (hours === 0 && minutes === 0) {
    return theme.palette.secondary.main;
  }
  return theme.palette.banner.background;
}

function formatDuration(hours: number, minutes: number) {
  return `${hours.toString().padStart(2, "0")}h ${minutes
    .toString()
    .padStart(2, "0")} Mins`;
}

export function IncidentDurationInner({
  label,
  dateTimeLeft,
  arrivalAtFirstPlaceOfSafetyDateTime,
  incidentWorkItem,
}: IncidentDurationProps) {
  const theme = useTheme();
  const translationOptions = {
    context: getWorkflowTranslationContext({ incidentWorkItem }),
  };

  let hours: number;
  let minutes: number;
  if (!arrivalAtFirstPlaceOfSafetyDateTime || !dateTimeLeft) {
    hours = 0;
    minutes = 0;
  } else {
    const durationParts = getDurationParts(
      dateTimeLeft,
      arrivalAtFirstPlaceOfSafetyDateTime,
    );
    hours = durationParts.hours;
    minutes = durationParts.minutes;
  }

  const bgColor = getDurationBgColor(hours, minutes, theme);
  const formattedDuration = formatDuration(hours, minutes);

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        alignItems: "center",
        justifyContent: "flex-start",
        marginBottom: "0.5rem",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        {label && <FormLabel {...label} />}
        <TypographyI18N
          translationKey={tk("finishForm.incidentDurationExplainer")}
          translationOptions={translationOptions}
        />
        <Box
          data-testid="incident-duration"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            backgroundColor: bgColor,
            padding: "1rem 0.5rem",
            borderRadius: "4px",
            marginTop: "1rem",
          }}
        >
          <AccessTime />
          <p style={{ margin: 0 }}>{formattedDuration}</p>
        </Box>
      </Box>
    </Box>
  );
}

export default function IncidentDuration({
  label,
  dateTimeLeft,
  arrivalAtFirstPlaceOfSafetyDateTime,
  incidentWorkItem,
}: IncidentDurationProps) {
  return incidentWorkItem === undefined ? (
    <CircularProgress />
  ) : (
    <IncidentDurationInner
      label={label}
      dateTimeLeft={dateTimeLeft}
      arrivalAtFirstPlaceOfSafetyDateTime={arrivalAtFirstPlaceOfSafetyDateTime}
      incidentWorkItem={incidentWorkItem}
    />
  );
}
